import { ApolloProvider } from '@apollo/client';
import { useBasename as historyUseBasename } from 'history';
import { useEffect } from 'react';
import { render } from 'react-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { browserHistory, Router } from 'react-router';
import './index.css';
import './../../static/styles/main.legacy.scss';
import './../../static/styles/main.legacy-tweaked.scss';
import { apolloClient } from './api/graphql/client';
import { routes } from './routes';

import * as Sentry from '@sentry/react';
import TagManager from 'react-gtm-module';

import LogRocket from 'logrocket';
import { Alert, ThemeProvider } from '@mui/material';
import { createTheme } from './new/themes';
import auth from './components/auth';
import { VideoUploadStateModal } from './new/pages/creator/library/views/videoUpload/VideoUploadStateModal';
import {
  getSessionId,
  registerSessionSubscribers,
  trackSessionEvent,
} from '@services/session-tracker/sessionTracker';
import { Typography } from '@design-system/components/typography/typography';
import { identifySentry, initSentry } from './sentry';

if (import.meta.env.VITE_ENV === 'production') {
  const tagManagerArgs = {
    gtmId: 'GTM-TWWN56K',
  };

  TagManager.initialize(tagManagerArgs);
}

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).data = {
  environment: import.meta.env.VITE_ENV || 'development',
};

const history = historyUseBasename(() => browserHistory)({
  basename: import.meta.env.BASE_URL,
});

const showDevtools = import.meta.env.VITE_ENV === 'development';

function fallbackRender({ error }) {
  // Call resetErrorBoundary() to reset the error boundary and retry the render.
  return (
    <div
      role="alert"
      className="flex justify-center w-screen h-screen bg-white max-w-screen mt-[20vh]"
    >
      <div>
        <Typography variant="headline" size="large" className="mb-10">
          Unexpected error happened
        </Typography>
        <Typography variant="bodyLarge" className="mb-10">
          We are sorry for inconvenience. <br />
          Please contact our{' '}
          <a
            href="mailto:creatorsupport@playbookapp.io"
            className="text-black underline"
          >
            support team
          </a>{' '}
          and provide this session id: <b>{getSessionId()}</b>
          <br />
          We will try to resolve this issue quickly.
        </Typography>
        <Alert severity="error">
          <div style={{ color: 'red' }} className="whitespace-pre-wrap">
            ERROR {auth.getUser() ? auth.getUser().influencerId : '(no user)'}{' '}
            at {window.location.pathname}: {error.message}
          </div>
        </Alert>
      </div>
    </div>
  );
}

const App = () => {
  const user = getUserFromLS();
  useEffect(registerSessionSubscribers, []);
  useEffect(() => {
    identifyLogRocket(user);
  }, [user]);
  useEffect(() => {
    identifySentry(user);
  }, [user]);
  useEffect(() => {
    const handler = window.addEventListener('User.login()', () => {
      const user = getUserFromLS();
      identifySentry(user);
      identifyLogRocket(user);
    });

    return () => {
      if (window.removeEventListener)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).removeEventListener('User.login()', handler);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={fallbackRender}>
      <ThemeProvider theme={createTheme()}>
        <ApolloProvider client={apolloClient}>
          <QueryClientProvider client={queryClient}>
            {showDevtools && <ReactQueryDevtools initialIsOpen={false} />}
            <Router
              history={history}
              routes={routes}
              onUpdate={() =>
                trackSessionEvent('Navigate', {
                  path: window.location.pathname,
                })
              }
            />
            <VideoUploadStateModal />
          </QueryClientProvider>
        </ApolloProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
};

render(<App />, document.getElementById('app'));

function getUserFromLS() {
  try {
    return JSON.parse(window.localStorage.getItem('user'));
  } catch (e) {
    console.error("Couldn't parse user from local storage", e);
    return null;
  }
}

function identifyLogRocket(user) {
  if (import.meta.env.VITE_ENV !== 'production') return;

  if (user && user.influencer) {
    LogRocket.init('9oooat/creator-admin-panel');

    const creatorName = user.influencer
      ? user.influencer.firstName + user.influencer.lastName
      : '';

    LogRocket.identify(user.portalUserId, {
      name:
        import.meta.env.VITE_ENV +
        ' | ' +
        user.firstName +
        ' ' +
        user.lastName +
        (creatorName ? ' (' + creatorName + ')' : ''),
      email: user.email,
      influencerId: user.influencerId,
      influencer: user.influencer,
      role: user.role,
    });
    console.info('Identified creator', user);
  }
}
